import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Input } from '@/components/ui/input'
import {
  Upload,
  Sparkles,
  CheckCircle2,
  AlertTriangle,
  RefreshCw,
  PenLine,
  Loader2,
} from 'lucide-react'
import {
  identifyApplianceAndLabel,
  type ApplianceIdentificationData,
} from '@/services/appliance-identification.api'
import { generateVisualGuide, getLabelGuide } from '@/services/asset.api'

type MismatchChoice = 'switchToDetected' | 'keepSelected' | 'reUpload'

// â”€â”€ State machine â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
type PhotoState = 'idle' | 'loading' | 'success' | 'qualityError' | 'mismatch' | 'manualMode'

// â”€â”€ Mismatch detection â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const SYNONYM_GROUPS: string[][] = [
  ['refrigerator', 'fridge', 'french door', 'side-by-side'],
  ['washing machine', 'washer', 'front load washer', 'top load washer'],
  ['dryer', 'tumble dryer', 'clothes dryer'],
  ['oven', 'range', 'stove', 'cooktop', 'double oven'],
  ['dishwasher', 'dish washer'],
  ['microwave', 'microwave oven'],
  ['air conditioner', 'ac unit', 'air conditioning', 'hvac', 'heat pump', 'mini split'],
  ['water heater', 'hot water heater', 'tankless water heater'],
  ['freezer', 'chest freezer', 'upright freezer'],
  ['tv', 'television', 'smart tv'],
  ['laptop', 'notebook'],
  ['computer', 'desktop', 'pc'],
]

function isMismatch(hint: string | undefined, detected: string | undefined): boolean {
  if (!hint?.trim() || !detected?.trim()) return false
  const h = hint.toLowerCase().trim()
  const d = detected.toLowerCase().trim()
  if (h === d) return false
  if (h.includes(d) || d.includes(h)) return false
  for (const group of SYNONYM_GROUPS) {
    if (group.some((s) => h.includes(s)) && group.some((s) => d.includes(s))) return false
  }
  return true
}

const LOADING_MESSAGE_KEYS = [
  'findLabel.loadingChecking',
  'findLabel.loadingIdentifying',
  'findLabel.loadingLookingUp',
  'findLabel.loadingBuilding',
] as const

const MAX_SIZE_BYTES = 5 * 1024 * 1024 // 5 MB

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result as string
      // Strip the data URI prefix â€” backend expects raw base64
      resolve(result.split(',')[1] ?? result)
    }
    reader.onerror = () => reject(new Error('Failed to read file'))
    reader.readAsDataURL(file)
  })
}

// â”€â”€ Props â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
interface FindLabelPhotoSectionProps {
  assetTypeHint?: string
  /** Called when identification is confirmed. */
  onIdentified?: (data: ApplianceIdentificationData) => void
}

// â”€â”€ Component â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
export function FindLabelPhotoSection({
  assetTypeHint,
  onIdentified,
}: Readonly<FindLabelPhotoSectionProps>) {
  const { t } = useTranslation('addAsset')
  const fileInputRef = useRef<HTMLInputElement>(null)
  const loadingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const [photoState, setPhotoState] = useState<PhotoState>('idle')
  const [result, setResult] = useState<ApplianceIdentificationData | null>(null)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [loadingMsgIdx, setLoadingMsgIdx] = useState(0)
  const [manualBrand, setManualBrand] = useState('')
  const [isLoadingManual, setIsLoadingManual] = useState(false)
  const [visualGuideUrl, setVisualGuideUrl] = useState<string>('')
  const [isLoadingVisualGuide, setIsLoadingVisualGuide] = useState(false)

  function viewInstructionFor(zone?: string): string {
    switch (zone) {
      case 'bottom':
        return 'Flip the device over — the label is on the underside.'
      case 'insideDoor':
        return 'Open the door — the label is on the inner door surface.'
      case 'side':
        return 'Check the side panel — the label is on the side.'
      case 'top':
        return 'Look on top — the label is on the top surface.'
      default:
        return 'Look at the back — the label is on the rear panel.'
    }
  }

  function clearVisualGuide() {
    setVisualGuideUrl((prev) => {
      if (prev.startsWith('blob:')) {
        URL.revokeObjectURL(prev)
      }
      return ''
    })
    setIsLoadingVisualGuide(false)
  }

  async function loadVisualGuide(params: {
    brand: string
    category: string
    subCategory: string
    assetType?: string
    wireframe?: { labelZone?: string }
  }) {
    const { brand, category, subCategory, assetType, wireframe } = params
    if (!brand || !category) {
      clearVisualGuide()
      return
    }

    setIsLoadingVisualGuide(true)
    try {
      const guideUrl = await generateVisualGuide(
        brand,
        category,
        subCategory,
        assetType,
        wireframe,
      )
      setVisualGuideUrl((prev) => {
        if (prev.startsWith('blob:')) {
          URL.revokeObjectURL(prev)
        }
        return guideUrl || ''
      })
    } catch {
      clearVisualGuide()
    } finally {
      setIsLoadingVisualGuide(false)
    }
  }

  // â”€â”€ Loading timer â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  function startLoadingTimer() {
    if (loadingTimerRef.current) clearInterval(loadingTimerRef.current)
    setLoadingMsgIdx(0)
    loadingTimerRef.current = setInterval(() => {
      setLoadingMsgIdx((i) => (i + 1) % LOADING_MESSAGE_KEYS.length)
    }, 4000)
  }

  function stopLoadingTimer() {
    if (loadingTimerRef.current) {
      clearInterval(loadingTimerRef.current)
      loadingTimerRef.current = null
    }
  }

  // â”€â”€ Photo handling â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return

    // Reset input so the same file can be re-selected after an error
    e.target.value = ''

    if (file.size > MAX_SIZE_BYTES) {
      setErrorMessage(t('findLabel.imageTooLarge'))
      setPhotoState('qualityError')
      return
    }

    try {
      setPhotoState('loading')
      startLoadingTimer()

      const base64 = await fileToBase64(file)
      const data = await identifyApplianceAndLabel({
        image: base64,
        assetTypeHint: assetTypeHint ?? undefined,
      })

      stopLoadingTimer()

      // Quality gate â€” only block on hard quality failures
      const HARD_QUALITY_ISSUES = ['too_dark', 'too_blurry', 'multiple_appliances']
      if (data.qualityIssue && HARD_QUALITY_ISSUES.includes(data.qualityIssue)) {
        setErrorMessage(
          data.qualityTip ?? t('findLabel.qualityFallback'),
        )
        setPhotoState('qualityError')
        return
      }

      setResult(data)

      // â”€â”€ Mismatch detection â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
      if (isMismatch(assetTypeHint, data.assetType)) {
        setPhotoState('mismatch')
        return
      }

      // Show success for high/medium confidence or when we have label steps
      if (data.labelSteps.length > 0 || data.confidence === 'high' || data.confidence === 'medium') {
        onIdentified?.(data)
        const detectedType = data.assetType || assetTypeHint || ''
        await loadVisualGuide({
          brand: data.brand || '',
          category: detectedType,
          subCategory: detectedType,
          assetType: detectedType,
          wireframe: data.wireframe,
        })
        setPhotoState('success')
      } else {
        // Low confidence without label steps â€” ask user to retry
        setErrorMessage(
          t('findLabel.lowConfidence'),
        )
        setPhotoState('qualityError')
      }
    } catch (err) {
      stopLoadingTimer()
      const msg =
        err instanceof Error ? err.message : t('findLabel.genericError')
      setErrorMessage(msg)
      setPhotoState('qualityError')
    }
  }

  // â”€â”€ User action handlers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  function handleNotRight() {
    clearVisualGuide()
    setPhotoState('idle')
    setResult(null)
  }

  function handleSkip() {
    clearVisualGuide()
    setManualBrand('')
    setPhotoState('manualMode')
  }

  async function handleManualSubmit() {
    const brand = manualBrand.trim()
    if (!brand) return
    try {
      setIsLoadingManual(true)
      const instructions = await getLabelGuide(brand, assetTypeHint || '', assetTypeHint || '')
      const identified: ApplianceIdentificationData = {
        brand: instructions.brand || brand,
        assetType: assetTypeHint,
        confidence: 'high',
        labelLocations: instructions.alternateLocations ?? [],
        labelSteps: instructions.steps ?? [],
        identifiedFrom: 'manual',
      }
      setResult(identified)
      onIdentified?.(identified)
      await loadVisualGuide({
        brand: identified.brand || brand,
        category: assetTypeHint || '',
        subCategory: assetTypeHint || '',
        assetType: assetTypeHint || '',
      })
      setPhotoState('success')
    } catch {
      setErrorMessage(t('findLabel.couldNotFetch'))
      setPhotoState('qualityError')
    } finally {
      setIsLoadingManual(false)
    }
  }

  function handleRetry() {
    clearVisualGuide()
    setPhotoState('idle')
    setResult(null)
    setErrorMessage('')
  }

  function handleCancel() {
    stopLoadingTimer()
    clearVisualGuide()
    setPhotoState('idle')
    setResult(null)
  }

  function handleMismatchChoice(choice: MismatchChoice) {
    const data = result!
    switch (choice) {
      case 'reUpload':
        clearVisualGuide()
        setPhotoState('idle')
        setResult(null)
        break

      case 'switchToDetected':
        onIdentified?.(data)
        void loadVisualGuide({
          brand: data.brand || '',
          category: data.assetType || assetTypeHint || '',
          subCategory: data.assetType || assetTypeHint || '',
          assetType: data.assetType || assetTypeHint || '',
          wireframe: data.wireframe,
        })
        setPhotoState('success')
        break

      case 'keepSelected':
        onIdentified?.({ ...data, assetType: assetTypeHint })
        void loadVisualGuide({
          brand: data.brand || '',
          category: assetTypeHint || '',
          subCategory: assetTypeHint || '',
          assetType: assetTypeHint || '',
          wireframe: data.wireframe,
        })
        setPhotoState('success')
        break
    }
  }

  // â”€â”€ Render â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  return (
    <div className="space-y-0">

      {/* ── Idle ─────────────────────────────────────────────────────── */}
      {photoState === 'idle' && (
        <div className="space-y-3">
          <label className="group block cursor-pointer">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileChange}
            />
            <div className="relative overflow-hidden rounded-xl border-2 border-dashed border-[hsl(var(--brand-primary))]/30 bg-gradient-to-br from-[hsl(var(--brand-primary))]/5 via-background to-[hsl(var(--brand-primary))]/3 p-6 transition-all duration-200 group-hover:border-[hsl(var(--brand-primary))]/55 group-hover:from-[hsl(var(--brand-primary))]/8 group-hover:shadow-sm">
              {/* Decorative blur circle */}
              <div className="pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full bg-[hsl(var(--brand-primary))]/8 blur-2xl" />
              <div className="relative flex flex-col items-center gap-3 text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--brand-primary))]/10 ring-4 ring-[hsl(var(--brand-primary))]/5 transition-transform duration-200 group-hover:scale-105">
                  <Upload className="h-5 w-5 text-[hsl(var(--brand-primary))]" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">{t('findLabel.snapOrUpload')}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {t('findLabel.snapOrUploadHelp', { type: assetTypeHint ?? t('findLabel.appliance') })}
                  </p>
                </div>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--brand-primary))] px-4 py-1.5 text-xs font-semibold text-white shadow-sm transition-opacity group-hover:opacity-90">
                  <Upload className="h-3 w-3" /> {t('findLabel.choosePhoto')}
                </span>
                <p className="text-[11px] text-muted-foreground/60">{t('findLabel.photoFormats')}</p>
              </div>
            </div>
          </label>
          <button
            type="button"
            onClick={handleSkip}
            className="w-full text-center text-xs text-muted-foreground underline underline-offset-2 transition-colors hover:text-foreground"
          >
            {t('findLabel.noPhotoCta')}
          </button>
        </div>
      )}

      {/* ── Loading ───────────────────────────────────────────────────── */}
      {photoState === 'loading' && (
        <div className="rounded-xl border border-border bg-muted/20 px-5 py-8">
          <div className="flex flex-col items-center gap-4 text-center">
            <div className="relative flex h-12 w-12 items-center justify-center">
              <div className="absolute inset-0 animate-ping rounded-full bg-[hsl(var(--brand-primary))]/15" />
              <Loader2 className="h-7 w-7 animate-spin text-[hsl(var(--brand-primary))]" />
            </div>
            <div>
              <p className="text-sm font-medium text-foreground">{t(LOADING_MESSAGE_KEYS[loadingMsgIdx])}</p>
              <p className="mt-0.5 text-xs text-muted-foreground">{t('findLabel.takesAFewSeconds')}</p>
            </div>
            <button
              type="button"
              onClick={handleCancel}
              className="text-xs text-muted-foreground underline underline-offset-2 transition-colors hover:text-foreground"
            >
              {t('findLabel.cancel')}
            </button>
          </div>
        </div>
      )}

      {/* ── Mismatch ─────────────────────────────────────────────────── */}
      {photoState === 'mismatch' && result && (
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          {/* Header */}
          <div className="flex items-center gap-2.5 border-b border-border px-4 py-3">
            <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--brand-primary))]/10">
              <Sparkles className="h-3 w-3 text-[hsl(var(--brand-primary))]" />
            </div>
            <p className="text-sm font-semibold text-foreground">{t('findLabel.detectedDifferent')}</p>
          </div>
          {/* Comparison — horizontal pill row */}
          <div className="flex items-center gap-2 px-4 py-3.5">
            <div className="flex flex-1 flex-col">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/60">{t('findLabel.yourSelection')}</p>
              <p className="mt-0.5 text-sm font-medium text-muted-foreground">{assetTypeHint}</p>
            </div>
            <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
              <span className="text-[11px] font-bold">{t('findLabel.vs')}</span>
            </div>
            <div className="flex flex-1 flex-col items-end text-right">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-[hsl(var(--brand-primary))]/70">{t('findLabel.aiDetected')}</p>
              <p className="mt-0.5 text-sm font-semibold text-foreground">{result.assetType}{result.brand ? <span className="font-normal text-muted-foreground"> · {result.brand}</span> : ''}</p>
            </div>
          </div>
          {/* 2 actions */}
          <div className="grid grid-cols-2 gap-2 border-t border-border px-4 py-3">
            <button
              type="button"
              onClick={() => handleMismatchChoice('switchToDetected')}
              className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-[hsl(var(--brand-primary))] px-3 py-2 text-xs font-semibold text-white shadow-sm transition-opacity hover:opacity-90"
            >
              <CheckCircle2 className="h-3.5 w-3.5" /> {t('findLabel.updateTo', { type: result.assetType })}
            </button>
            <button
              type="button"
              onClick={() => handleMismatchChoice('reUpload')}
              className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-border bg-background px-3 py-2 text-xs font-medium text-foreground transition-colors hover:bg-muted/50"
            >
              <RefreshCw className="h-3 w-3" /> {t('findLabel.uploadAgain')}
            </button>
          </div>
        </div>
      )}

      {/* ── Success ───────────────────────────────────────────────────── */}
      {photoState === 'success' && result && (
        <div className="space-y-4">
          {/* ID confirmation banner */}
          <div className="flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 dark:border-emerald-800 dark:bg-emerald-950/30">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-500/15">
              <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-emerald-900 dark:text-emerald-100">
                {result.brand}{result.assetType ? <span className="text-emerald-700 dark:text-emerald-300"> · {result.assetType}</span> : ''}
              </p>
              <p className="text-xs text-emerald-700/70 dark:text-emerald-300/70">{t('findLabel.applianceIdentifiedReady')}</p>
            </div>
          </div>

          {/* Steps */}
          {result.labelSteps.length > 0 && (
            <div className="rounded-xl border border-border bg-card p-4">
              <div className="mb-3 flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[hsl(var(--brand-primary))]" />
                <p className="text-sm font-semibold text-foreground">{t('findLabel.howToFindLabel')}</p>
              </div>
              <ol className="space-y-3">
                {result.labelSteps.map((step, i) => (
                  <li key={step} className="flex gap-3">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--brand-primary))]/10 text-[10px] font-bold text-[hsl(var(--brand-primary))]">
                      {i + 1}
                    </span>
                    <span className="text-sm leading-relaxed text-foreground">{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {(isLoadingVisualGuide || visualGuideUrl) && (
            <div className="rounded-xl border border-border bg-card p-4">
              <div className="mb-3">
                <p className="text-sm font-semibold text-foreground">Product Label Location</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {viewInstructionFor(result.wireframe?.labelZone)}
                </p>
              </div>

              {isLoadingVisualGuide && !visualGuideUrl && (
                <div className="h-48 w-full rounded-lg border border-dashed border-border bg-muted/20 flex items-center justify-center">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Generating diagram...</span>
                  </div>
                </div>
              )}

              {visualGuideUrl && (
                <div className="rounded-lg border border-border/60 bg-white overflow-hidden">
                  <img
                    src={visualGuideUrl}
                    alt="Product label location wireframe"
                    className="w-full max-h-56 object-contain"
                  />
                </div>
              )}
            </div>
          )}

          {/* Back button */}
          <button
            type="button"
            onClick={handleNotRight}
            className="inline-flex items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
          >
            <RefreshCw className="h-3 w-3" /> {t('findLabel.scanDifferent')}
          </button>
        </div>
      )}

      {/* ── Quality / generic error ───────────────────────────────────── */}
      {photoState === 'qualityError' && (
        <div className="overflow-hidden rounded-xl border border-destructive/25 bg-destructive/5">
          <div className="flex items-start gap-3 px-4 py-3.5">
            <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-destructive/10">
              <AlertTriangle className="h-3.5 w-3.5 text-destructive" />
            </div>
            <div>
              <p className="text-sm font-semibold text-destructive">{t('findLabel.photoNotUsable')}</p>
              <p className="mt-0.5 text-xs leading-relaxed text-destructive/80">
                {errorMessage || t('findLabel.genericError')}
              </p>
            </div>
          </div>
          <div className="border-t border-destructive/15 px-4 py-2.5">
            <button
              type="button"
              onClick={handleRetry}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-[hsl(var(--brand-primary))] transition-opacity hover:opacity-80"
            >
              <RefreshCw className="h-3.5 w-3.5" /> {t('findLabel.tryDifferent')}
            </button>
          </div>
        </div>
      )}

      {/* ── Manual mode ──────────────────────────────────────────────── */}
      {photoState === 'manualMode' && (
        <div className="rounded-xl border border-border bg-card p-4 space-y-3">
          <div className="flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-[hsl(var(--brand-primary))]/10">
              <PenLine className="h-3.5 w-3.5 text-[hsl(var(--brand-primary))]" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">{t('findLabel.enterBrand')}</p>
              <p className="text-xs text-muted-foreground">{t('findLabel.pullUpLocation')}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Input
              value={manualBrand}
              onChange={(e) => setManualBrand(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleManualSubmit()}
              placeholder={t('findLabel.brandPlaceholder')}
              className="flex-1 text-sm"
              autoFocus
            />
            <button
              type="button"
              onClick={handleManualSubmit}
              disabled={!manualBrand.trim() || isLoadingManual}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-[hsl(var(--brand-primary))] px-4 py-2 text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isLoadingManual
                ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> {t('findLabel.loading')}</>
                : <><Sparkles className="h-3.5 w-3.5" /> {t('findLabel.getInstructions')}</>
              }
            </button>
          </div>
          <button
            type="button"
            onClick={handleRetry}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
          >
            {t('findLabel.backToPhoto')}
          </button>
        </div>
      )}
    </div>
  )
}
